/**
 * Terrain Features & Scene Maps
 * Data-driven terrain system for the combat grid.
 * Each position can have one terrain feature from the catalog.
 */

import type { PositionId, MapTemplate } from './grid';

// ============================================================================
// TYPES
// ============================================================================

export type TerrainFeatureType =
  | 'cover_light'   // Bushes, crates, low walls — reduces attacker pool by 1
  | 'cover_heavy'   // Boulders, thick trees, pillars — reduces attacker pool by 2
  | 'obstacle'      // Impassable: buildings, cliffs, deep pits
  | 'difficult'     // Costs extra AP to enter: rubble, mud, undergrowth
  | 'hazard'        // Deals damage at round start: fire, acid, thorns
  | 'elevated';     // High ground: +1 die to ranged/spell attacks FROM here

export interface TerrainFeature {
  type: TerrainFeatureType;
  name: string;
  description: string;
  icon: string;            // Lucide icon name
  moveCostExtra?: number;  // Only for 'difficult' — extra AP to enter (default 1)
  hazardDamage?: number;   // Only for 'hazard' — damage per round (default 1)
}

/** Position ID → terrain catalog key */
export type TerrainMap = Partial<Record<PositionId, string>>;

export interface SceneMap {
  id: string;
  name: string;
  description: string;
  category: 'wilderness' | 'dungeon' | 'urban' | 'special';
  baseTemplate: MapTemplate;
  terrain: TerrainMap;
}

// ============================================================================
// TERRAIN CATALOG
// ============================================================================

export const TERRAIN_CATALOG: Record<string, TerrainFeature> = {
  // --- Cover (heavy) ---
  tree:     { type: 'cover_heavy', name: 'Tree',    description: 'A sturdy tree provides solid cover from ranged attacks',   icon: 'TreePine' },
  boulder:  { type: 'cover_heavy', name: 'Boulder', description: 'A large boulder blocks incoming projectiles',              icon: 'Mountain' },
  pillar:   { type: 'cover_heavy', name: 'Pillar',  description: 'A stone pillar offers reliable cover in all directions',   icon: 'Cylinder' },

  // --- Cover (light) ---
  crates:   { type: 'cover_light', name: 'Crates',   description: 'Stacked crates provide partial concealment',       icon: 'Box' },
  low_wall: { type: 'cover_light', name: 'Low Wall', description: 'A crumbling wall offers some protection',           icon: 'Minus' },
  well:     { type: 'cover_light', name: 'Well',     description: 'A stone well provides limited cover to duck behind', icon: 'Droplet' },

  // --- Obstacles ---
  building: { type: 'obstacle', name: 'Building',    description: 'A solid structure blocks all movement',  icon: 'Home' },
  cliff:    { type: 'obstacle', name: 'Cliff Edge',  description: 'A sheer drop — no way across',           icon: 'TriangleAlert' },
  deep_pit: { type: 'obstacle', name: 'Deep Pit',    description: 'A gaping pit too wide to cross',         icon: 'CircleOff' },

  // --- Difficult terrain ---
  rubble:        { type: 'difficult', name: 'Rubble',        description: 'Broken stone slows your footing',         icon: 'Layers',   moveCostExtra: 1 },
  mud:           { type: 'difficult', name: 'Thick Mud',     description: 'Heavy mud that saps energy to wade through', icon: 'Droplets', moveCostExtra: 2 },
  undergrowth:   { type: 'difficult', name: 'Undergrowth',   description: 'Dense brush that tangles legs',            icon: 'Shrub',    moveCostExtra: 1 },
  shallow_water: { type: 'difficult', name: 'Shallow Water', description: 'Ankle-deep water that slows movement',     icon: 'Waves',    moveCostExtra: 1 },

  // --- Hazards ---
  fire_pit:  { type: 'hazard', name: 'Fire Pit',    description: 'A pit of burning coals that scorches anyone nearby', icon: 'Flame',      hazardDamage: 2 },
  acid_pool: { type: 'hazard', name: 'Acid Pool',   description: 'A puddle of corrosive liquid',                       icon: 'FlaskRound', hazardDamage: 1 },
  thorns:    { type: 'hazard', name: 'Thorn Patch',  description: 'Razor-sharp thorns that cut exposed flesh',          icon: 'Sparkles',   hazardDamage: 1 },

  // --- Elevated ---
  ledge:   { type: 'elevated', name: 'Ledge',   description: 'A raised ledge granting high-ground advantage to ranged attacks', icon: 'ArrowUpFromLine' },
  hillock: { type: 'elevated', name: 'Hillock', description: 'A small hill offering a vantage point',                          icon: 'TrendingUp' },
};

/** Cover penalty by type: subtracted from attacker's ranged dice pool */
export const TERRAIN_EFFECTS = {
  cover_light_penalty: 1,
  cover_heavy_penalty: 2,
  elevated_bonus: 1,
  default_move_cost_extra: 1,
  default_hazard_damage: 1,
} as const;

// ============================================================================
// SCENE MAPS
// ============================================================================

export const SCENE_MAPS: Record<string, SceneMap> = {
  forest_clearing: {
    id: 'forest_clearing',
    name: 'Forest Clearing',
    description: 'A ring of trees and undergrowth surrounds an open clearing. Forces close combat or approaching through cover.',
    category: 'wilderness',
    baseTemplate: 'wide_area',
    terrain: {
      N2: 'tree',
      NE2: 'undergrowth',
      E2: 'tree',
      SE2: 'undergrowth',
      S2: 'tree',
      SW2: 'undergrowth',
      W2: 'tree',
      NW2: 'undergrowth',
    },
  },

  ruined_courtyard: {
    id: 'ruined_courtyard',
    name: 'Ruined Courtyard',
    description: 'Low walls and pillars provide cover amid rubble. Two buildings block flanking routes.',
    category: 'urban',
    baseTemplate: 'wide_area',
    terrain: {
      N1: 'low_wall',
      NE2: 'building',
      E1: 'pillar',
      SE1: 'rubble',
      S2: 'low_wall',
      SW2: 'building',
      W1: 'pillar',
      NW1: 'rubble',
    },
  },

  burning_hall: {
    id: 'burning_hall',
    name: 'Burning Hall',
    description: 'A hall aflame. Fire pits block several positions, while pillars offer the only cover.',
    category: 'dungeon',
    baseTemplate: 'wide_area',
    terrain: {
      NE1: 'fire_pit',
      SE1: 'fire_pit',
      SW1: 'fire_pit',
      N1: 'pillar',
      E2: 'pillar',
      W2: 'pillar',
      S1: 'fire_pit',
      NW2: 'crates',
    },
  },

  riverside_camp: {
    id: 'riverside_camp',
    name: 'Riverside Camp',
    description: 'A camp by the river. The stream runs east, boulders and trees provide cover, and a hillock grants high ground.',
    category: 'wilderness',
    baseTemplate: 'open_field',
    terrain: {
      E1: 'shallow_water',
      E2: 'shallow_water',
      E3: 'shallow_water',
      N3: 'hillock',
      NW2: 'boulder',
      W2: 'tree',
      SW2: 'tree',
      S3: 'boulder',
      NE2: 'crates',
    },
  },

  narrow_bridge: {
    id: 'narrow_bridge',
    name: 'Narrow Bridge',
    description: 'A tight corridor with a ledge at the far end and a low wall at the entry. Tests terrain in a constrained layout.',
    category: 'special',
    baseTemplate: 'corridor',
    terrain: {
      S1: 'low_wall',
      N3: 'ledge',
    },
  },
};

export const SCENE_MAP_LABELS: Record<string, string> = Object.fromEntries(
  Object.values(SCENE_MAPS).map(s => [s.id, s.name])
);

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/** Look up the terrain feature at a position, or null if none */
export function getTerrainAt(terrain: Record<string, string>, pos: PositionId): TerrainFeature | null {
  const key = terrain[pos];
  if (!key) return null;
  return TERRAIN_CATALOG[key] ?? null;
}

/** Cover penalty for a defender at the given position (0 / 1 / 2) */
export function getCoverPenalty(terrain: Record<string, string>, defenderPos: PositionId): number {
  const feature = getTerrainAt(terrain, defenderPos);
  if (!feature) return 0;
  if (feature.type === 'cover_heavy') return TERRAIN_EFFECTS.cover_heavy_penalty;
  if (feature.type === 'cover_light') return TERRAIN_EFFECTS.cover_light_penalty;
  return 0;
}

/** Extra AP cost to enter a position (0 / 1 / 2) */
export function getMoveCostExtra(terrain: Record<string, string>, targetPos: PositionId): number {
  const feature = getTerrainAt(terrain, targetPos);
  if (!feature || feature.type !== 'difficult') return 0;
  return feature.moveCostExtra ?? TERRAIN_EFFECTS.default_move_cost_extra;
}

/** Hazard damage dealt at round start (0 / 1 / 2) */
export function getHazardDamage(terrain: Record<string, string>, pos: PositionId): number {
  const feature = getTerrainAt(terrain, pos);
  if (!feature || feature.type !== 'hazard') return 0;
  return feature.hazardDamage ?? TERRAIN_EFFECTS.default_hazard_damage;
}

/** Elevated bonus: +1 die if attacker is on elevated terrain and defender is not (and attack is non-melee) */
export function getElevatedBonus(terrain: Record<string, string>, attackerPos: PositionId, defenderPos: PositionId): number {
  const attackerFeature = getTerrainAt(terrain, attackerPos);
  const defenderFeature = getTerrainAt(terrain, defenderPos);
  if (attackerFeature?.type === 'elevated' && defenderFeature?.type !== 'elevated') {
    return TERRAIN_EFFECTS.elevated_bonus;
  }
  return 0;
}

/** Whether a position is an obstacle (impassable) */
export function isObstacle(terrain: Record<string, string>, pos: PositionId): boolean {
  const feature = getTerrainAt(terrain, pos);
  return feature?.type === 'obstacle';
}

/** Human-readable terrain name at a position, or empty string */
export function getTerrainName(terrain: Record<string, string>, pos: PositionId): string {
  const feature = getTerrainAt(terrain, pos);
  return feature?.name ?? '';
}
